import { NavLink } from 'react-router-dom';
import {
  Megaphone,
  ClipboardList,
  FileClock,
  LayoutGrid,
  ShieldCheck,
  Ticket,
  UserCog,
  Users,
} from 'lucide-react';
import type { CurrentUser } from '@electrodavisan/shared';
import { ROUTES } from '../../app/config';
import { cn } from '../../lib/cn';
import { Logo } from './Logo';
import { UserMenu } from './UserMenu';

interface NavItem {
  to: string;
  label: string;
  icon: JSX.Element;
  end?: boolean;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

/**
 * La navegacion se construye segun los roles que devuelve `/api/me`.
 * Ocultar un enlace es solo comodidad visual: cada endpoint vuelve a
 * comprobar el permiso en el servidor.
 *
 * Para quien no administra ni atiende soporte son DOS entradas: sus
 * aplicaciones y sus tickets. No hay «Inicio», porque el panel que habia ahi
 * no ensenaba nada que no estuviera ya en esas dos pantallas, y no hay «Crear
 * ticket», porque es una accion y ya tiene su boton en la barra superior y en
 * la propia lista de tickets. Un menu que repite lo que hay dos clics mas
 * abajo no orienta: solo hace mas largo el menu.
 */
function buildSections(user: CurrentUser | undefined): NavSection[] {
  const sections: NavSection[] = [
    {
      items: [
        { to: ROUTES.apps, label: 'Aplicaciones', icon: <LayoutGrid className="h-[18px] w-[18px]" /> },
        { to: ROUTES.tickets, label: 'Mis tickets', icon: <Ticket className="h-[18px] w-[18px]" /> },
        /*
         * Los anuncios van en el primer bloque, con lo que ve TODO el mundo, y
         * no en una seccion de administracion. Es la razon de ser del canal: si
         * hiciera falta un rol para verlo, la gente se enteraria por otro lado
         * y el canal sobraria.
         */
        {
          to: ROUTES.announcements,
          label: 'Anuncios',
          icon: <Megaphone className="h-[18px] w-[18px]" />,
        },
      ],
    },
  ];

  if (user?.isSupport) {
    sections.push({
      title: 'Mesa de ayuda',
      items: [
        {
          to: ROUTES.supportTickets,
          label: 'Tickets de soporte',
          icon: <ClipboardList className="h-[18px] w-[18px]" />,
        },
      ],
    });
  }

  if (user?.isAdmin) {
    sections.push({
      title: 'Administracion',
      items: [
        { to: ROUTES.adminApps, label: 'Aplicaciones', icon: <ShieldCheck className="h-[18px] w-[18px]" /> },
        { to: ROUTES.adminAccess, label: 'Accesos', icon: <UserCog className="h-[18px] w-[18px]" /> },
        /*
         * «Personas» es donde se cambian contrasenas de otros —que es poder
         * entrar en su cuenta— y donde se reparte a que aplicaciones entra
         * cada una.
         */
        ...(user.isSuperAdmin
          ? [
              {
                to: ROUTES.adminUsers,
                label: 'Personas',
                icon: <Users className="h-[18px] w-[18px]" />,
              },
            ]
          : []),
        { to: ROUTES.adminAudit, label: 'Auditoria', icon: <FileClock className="h-[18px] w-[18px]" /> },
      ],
    });
  }

  return sections;
}

export function SidebarContent({
  user,
  compact = false,
  onNavigate,
}: {
  user: CurrentUser | undefined;
  compact?: boolean;
  onNavigate?: () => void;
}): JSX.Element {
  const sections = buildSections(user);

  return (
    <div className="flex h-full flex-col bg-surface">
      {/*
        Sin `h-16` cuando esta desplegada: el isotipo va en pila con su
        palabra debajo y en una fila de cuatro rem se recortaria. Plegada si
        mantiene la altura fija, porque ahi solo hay un icono y tiene que
        alinearse con la barra superior.
      */}
      <div
        className={cn(
          'flex shrink-0 items-center justify-center overflow-hidden border-b border-border',
          compact ? 'h-16 px-2' : 'px-4 py-5',
        )}
      >
        <Logo compact={compact} />
      </div>

      <nav
        className="min-h-0 flex-1 space-y-6 overflow-y-auto px-3 py-4"
        aria-label="Navegacion principal"
      >
        {sections.map((section, index) => (
          <div key={section.title ?? `section-${index}`}>
            {section.title && !compact ? (
              <p className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                {section.title}
              </p>
            ) : null}
            <ul className="space-y-1">
              {section.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={onNavigate}
                    title={compact ? item.label : undefined}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
                        compact && 'justify-center px-0',
                        isActive
                          ? 'bg-primary-soft text-primary-bright'
                          : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                      )
                    }
                  >
                    <span className="shrink-0" aria-hidden>
                      {item.icon}
                    </span>
                    {!compact ? <span className="truncate">{item.label}</span> : null}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Perfil siempre abajo. */}
      <div className="shrink-0 border-t border-border p-3">
        <UserMenu user={user} compact={compact} onNavigate={onNavigate} />
      </div>
    </div>
  );
}
